import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDate } from "@/lib/constants";
import { Mail, PauseCircle, PlayCircle, Clock, CheckCircle2, AlertCircle, Ban } from "lucide-react";

const STEP_LABELS = { 1: "Erinnerung (24 h)", 2: "Mehrwert (3 Tage)", 3: "Letzter Anstoß (7 Tage)" };
const STATUS_BADGE = {
  sent: { label: "Versendet", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  mocked: { label: "Mock", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  failed: { label: "Fehlgeschlagen", cls: "bg-red-50 text-red-700 border-red-200" },
  skipped: { label: "Übersprungen", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  received: { label: "Eingegangen", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

function fmtStatus(s) {
  return STATUS_BADGE[s] || { label: s || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
}

function templateLabel(template, step) {
  if (template === "followup") return `Follow-up · Step ${step || "?"}/3`;
  if (template === "offer") return "Angebot";
  if (template === "invite") return "Einladung";
  if (template === "lead_reaction") return "Lead-Reaktion";
  if (template === "followup_optout") return "Follow-up-Abmeldung";
  return template || "E-Mail";
}

export default function LeadEmailFollowupCard({ offers, messages, onChanged }) {
  const [busy, setBusy] = useState("");
  // jüngstes versendetes Offer als primäres Subjekt der Follow-up-Sequenz
  const sentOffers = (offers || []).filter((o) => o.status === "Versendet");
  const offer = sentOffers.length
    ? sentOffers.reduce((a, b) => (new Date(a.sent_at || 0) > new Date(b.sent_at || 0) ? a : b))
    : null;

  // Alle E-Mail-Audit-Einträge (out) + Eingehende (z.B. Opt-out)
  const emailMessages = (messages || []).filter(
    (m) => !!m.email_template || ["E-Mail", "System"].includes(m.channel),
  );

  const statusInfo = (() => {
    if (!offer) return { label: "Inaktiv", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock };
    if (offer.followup_opt_out) return { label: "Abgemeldet", cls: "bg-red-50 text-red-700 border-red-200", icon: Ban };
    if (offer.followup_completed) return { label: "Abgeschlossen", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 };
    if (offer.followup_paused) return { label: "Pausiert", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: PauseCircle };
    return { label: "Aktiv", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: PlayCircle };
  })();

  const StatusIcon = statusInfo.icon;

  const togglePause = async (action) => {
    if (!offer) return;
    setBusy(action);
    try {
      const r = await api.post(`/offers/${offer.offer_id}/followup/${action}`);
      toast.success(action === "pause" ? "Follow-ups pausiert" : "Follow-ups fortgesetzt");
      if (onChanged) await onChanged(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally {
      setBusy("");
    }
  };

  return (
    <Card className="border-slate-200 p-6" data-testid="email-followup-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-orange-600" />
          <h3 className="text-lg font-semibold text-slate-900">E-Mail- &amp; Follow-up-Aktivität</h3>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${statusInfo.cls}`}
          data-testid="followup-status-badge"
        >
          <StatusIcon className="w-3 h-3" />
          {statusInfo.label}
        </span>
      </div>

      {!offer ? (
        <p className="text-sm text-slate-500">Sobald ein Angebot versendet wurde, erscheint hier die automatische Follow-up-Sequenz.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
            <Stat
              label="Aktueller Step"
              value={offer.followup_completed ? "Sequenz beendet" : `${offer.followup_step || 0} / 3`}
              testid="followup-step-value"
            />
            <Stat
              label="Nächste Mail"
              value={offer.followup_completed ? "—" : (offer.next_followup_at ? formatDate(offer.next_followup_at) : "—")}
              testid="followup-next-at"
            />
            <Stat
              label="Letzte Follow-up-Mail"
              value={offer.last_followup_sent_at ? formatDate(offer.last_followup_sent_at) : "—"}
            />
            <Stat
              label="Angebot versendet"
              value={offer.sent_at ? formatDate(offer.sent_at) : "—"}
            />
          </div>

          {offer.followup_error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-4" data-testid="followup-error-banner">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Letzter Versand fehlgeschlagen: {offer.followup_error}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-5">
            {!offer.followup_paused && !offer.followup_opt_out && !offer.followup_completed && (
              <Button
                data-testid="followup-pause-btn"
                size="sm"
                variant="outline"
                onClick={() => togglePause("pause")}
                disabled={busy === "pause"}
              >
                <PauseCircle className="w-4 h-4 mr-1.5" />
                {busy === "pause" ? "Pausiere …" : "Follow-ups pausieren"}
              </Button>
            )}
            {offer.followup_paused && !offer.followup_opt_out && !offer.followup_completed && (
              <Button
                data-testid="followup-resume-btn"
                size="sm"
                onClick={() => togglePause("resume")}
                disabled={busy === "resume"}
                className="bg-orange-600 hover:bg-orange-700 text-white"
              >
                <PlayCircle className="w-4 h-4 mr-1.5" />
                {busy === "resume" ? "Setze fort …" : "Follow-ups fortsetzen"}
              </Button>
            )}
          </div>
        </>
      )}

      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">E-Mail- &amp; Audit-Verlauf</p>
        {emailMessages.length === 0 ? (
          <p className="text-sm text-slate-500">Noch keine E-Mail-Aktivität.</p>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="email-history-list">
            {emailMessages.map((m) => {
              const status = fmtStatus(m.delivery_status);
              return (
                <li key={m.msg_id} className="px-3 py-2.5 text-sm flex items-start gap-3" data-testid={`email-history-${m.msg_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 truncate">{m.subject || "(ohne Betreff)"}</span>
                      <span className="text-xs text-slate-500">· {templateLabel(m.email_template, m.followup_step)}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{formatDate(m.created_at)}</span>
                      {m.to_email && <span>An: {m.to_email}</span>}
                      {m.followup_step ? <span>Step {m.followup_step}/3</span> : null}
                    </div>
                  </div>
                  <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${status.cls}`}>
                    {status.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className="text-sm text-slate-900 mt-0.5">{value}</p>
  </div>
);

// re-export for tests / external usage
export { STEP_LABELS };
